import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  AnyApiFactory,
  configApiRef,
  createApiFactory,
  OpenIdConnectApi,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
  discoveryApiRef,
  oauthRequestApiRef,
  ApiRef,
  createApiRef,
  fetchApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import { ScaffolderClient } from '@backstage/plugin-scaffolder';
import { isAdminRole } from './utils/roles';

export const keycloakAuthApiRef: ApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
> = createApiRef({
  id: 'auth.keycloak',
});

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: keycloakAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      OAuth2.create({
        configApi,
        discoveryApi,
        oauthRequestApi,
        provider: {
          id: 'keycloak',
          title: 'keycloak',
          icon: () => null,
        },
        environment: configApi.getOptionalString('auth.environment'),
        defaultScopes: ['openid', 'profile', 'email', 'groups'],
        // popupOptions: {
        //   // optional, used to customize login in popup size
        //   size: {
        //     fullscreen: true,
        //   },
        //   /**
        //    * or specify popup width and height
        //    * size: {
        //       width: 1000,
        //       height: 1000,
        //     }
        //    */
        // },
      }),
  }),
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  ScmAuth.createDefaultApiFactory(),
  createApiFactory({
    api: scaffolderApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
      identityApi: identityApiRef,
      scmIntegrationsApi: scmIntegrationsApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, fetchApi, identityApi, scmIntegrationsApi, configApi }) => {
      const client = new ScaffolderClient({
        discoveryApi,
        fetchApi,
        identityApi,
        scmIntegrationsApi,
      });
      const originalListTasks = client.listTasks.bind(client);

      /**
       * Tasks started by a git push, which the scaffolder cannot attribute.
       *
       * A service-created task gets `createdBy: undefined` -- the scaffolder
       * derives it from the caller's credentials, and AuthService has no
       * impersonation path, so there is nothing to set it to. Since the
       * "owned" filter below filters on exactly that column, a student's
       * push-triggered builds would otherwise be invisible to them.
       *
       * /api/app-push/tasks matches them server-side on the task's targetUser
       * parameter against the caller's own credentials, so this merge shows
       * each student their own automatic deploys and nobody else's.
       *
       * Best-effort: if the endpoint is unavailable (feature disabled, older
       * backend) the task list still renders, just without push deploys.
       */
      const listPushTasks = async (limit?: number) => {
        try {
          const baseUrl = await discoveryApi.getBaseUrl('app-push');
          const response = await fetchApi.fetch(
            `${baseUrl}/tasks?limit=${limit ?? 50}`,
          );
          if (!response.ok) return [];
          const { tasks } = await response.json();
          return Array.isArray(tasks) ? tasks : [];
        } catch {
          return [];
        }
      };

      /**
       * Fills in the owner the task ran for, when the scaffolder could not.
       *
       * The Owner column renders `spec.user.ref`, which the scaffolder builds
       * from the caller's credentials -- so it is undefined for any
       * service-created task and the column reads "unknown". The task's own
       * `targetUser` parameter is the answer, and it is already present on
       * every row the list API returns, so no extra request is needed.
       *
       * Applied to every row whatever its source: students see their own push
       * deploys attributed, and admins viewing "all" can tell whose app each
       * automatic build belongs to. Display only -- the stored task is never
       * modified.
       */
      const withOwner = (task: any) => {
        if (task?.spec?.user?.ref) return task;
        const target = task?.spec?.parameters?.targetUser;
        if (typeof target !== 'string' || !target) return task;
        const ref = target.includes(':') ? target : `user:default/${target}`;
        return {
          ...task,
          createdBy: task.createdBy ?? ref,
          spec: {
            ...task.spec,
            user: { ...(task.spec?.user ?? {}), ref },
          },
        };
      };

      client.listTasks = (async options => {
        try {
          const identity = await identityApi.getBackstageIdentity();
          const ownershipRefs = identity.ownershipEntityRefs ?? [];
          const isAdmin = isAdminRole(ownershipRefs, configApi);
          const enforcedFilter: 'owned' | 'all' = isAdmin
            ? options.filterByOwnership ?? 'all'
            : 'owned';

          const owned = await originalListTasks({
            ...options,
            filterByOwnership: enforcedFilter,
          });

          // Admins asking for 'all' already receive push-triggered tasks from
          // the scaffolder, so merging would duplicate every row -- but they
          // still arrive unattributed, hence the enrichment.
          if (enforcedFilter === 'all') {
            return { ...owned, tasks: (owned.tasks ?? []).map(withOwner) };
          }

          const pushed = await listPushTasks(options.limit);
          if (!pushed.length) {
            return { ...owned, tasks: (owned.tasks ?? []).map(withOwner) };
          }

          const seen = new Set((owned.tasks ?? []).map((t: any) => t.id));
          const merged = [...(owned.tasks ?? []), ...pushed.filter((t: any) => !seen.has(t.id))]
            .map(withOwner)
            .sort((a: any, b: any) =>
              String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? '')),
            );

          return {
            ...owned,
            tasks: merged,
            // Approximate: the two sources are paginated independently, so the
            // count is the merged page rather than a true grand total.
            totalTasks: (owned.totalTasks ?? 0) + pushed.length,
          };
        } catch (error) {
          // Fall back to original behavior if identity lookup fails
          return originalListTasks(options);
        }
      }) as typeof client.listTasks;
      return client;
    },
  }),
];
